/**
 * AlphaForm CRM — shared domain contract.
 *
 * SINGLE SOURCE OF TRUTH for both `backend/` and `dashboard/`.
 * Imported as `@shared/types` from either side (tsconfig paths + vite alias).
 *
 * Rules for contributors:
 *  - Never widen a field to `any`.
 *  - Wire format is JSON: dates are ISO-8601 strings, never Date objects.
 *  - Every list endpoint returns `Paginated<T>`; every error is `ApiError`.
 */

/* ────────────────────────────────────────────────────────────────────────────
   Primitives
   ──────────────────────────────────────────────────────────────────────────── */

export type ID = string;
/** ISO-8601 UTC, e.g. "2026-08-06T14:03:11.482Z" */
export type ISODate = string;

export interface Paginated<T> {
  items: T[];
  /** Opaque cursor for the next page; null when exhausted. */
  nextCursor: string | null;
  /** Total matching rows for the current filter set (not just this page). */
  total: number;
}

export interface ApiError {
  error: {
    code:
      | 'bad_request'
      | 'unauthorized'
      | 'forbidden'
      | 'not_found'
      | 'conflict'
      | 'unprocessable'
      | 'rate_limited'
      | 'internal';
    message: string;
    /** Field-level validation problems, keyed by dotted path. */
    fields?: Record<string, string>;
    requestId: string;
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Enumerations
   ──────────────────────────────────────────────────────────────────────────── */

export const ROLES = ['admin', 'manager', 'agent', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

/** Pipeline stages are configurable rows in `pipeline_stages`; these are the seeded keys. */
export const STAGE_KEYS = [
  'new',
  'triage',
  'qualifying',
  'review',
  'proposal',
  'won',
  'lost',
] as const;
export type StageKey = (typeof STAGE_KEYS)[number];

/** Lifecycle status, orthogonal to pipeline stage. */
export const STATUSES = ['open', 'won', 'lost', 'spam', 'archived'] as const;
export type Status = (typeof STATUSES)[number];

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const SCORE_BANDS = ['hot', 'warm', 'early', 'cold'] as const;
export type ScoreBand = (typeof SCORE_BANDS)[number];

/* ── Score bands — one definition ─────────────────────────────────────────────

   A lead's band is the product's central number and it used to mean four
   different things at once: the scoring engine banded at 75/50/25, the seeder
   at 75/55/35, the `settings` rows at 75/55/35 and the dashboard at 80/60/35.
   A lead scoring 78 rendered Hot in the list and read "WARM" on the record one
   click later.

   The cut-offs below are the only ones. They match the seeded
   `scoring.*_threshold` settings rows, which an admin may retune at runtime —
   pass the retuned set to `bandForScore` and the same rule applies to it.

   Ranges, inclusive of the lower bound:
     hot   75–100   funded, scoped and moving
     warm  55–74    real project, one or two unknowns left
     early 35–54    genuine interest, no timeline or budget yet
     cold   0–34    low signal
   ────────────────────────────────────────────────────────────────────────── */

export interface ScoreBandThresholds {
  hot: number;
  warm: number;
  early: number;
}

export const SCORE_BAND_THRESHOLDS: ScoreBandThresholds = { hot: 75, warm: 55, early: 35 };

/** The closed 0–100 range each band owns, derived from the one set of cut-offs. */
export const SCORE_BAND_RANGES: Record<ScoreBand, { min: number; max: number }> = {
  hot: { min: SCORE_BAND_THRESHOLDS.hot, max: 100 },
  warm: { min: SCORE_BAND_THRESHOLDS.warm, max: SCORE_BAND_THRESHOLDS.hot - 1 },
  early: { min: SCORE_BAND_THRESHOLDS.early, max: SCORE_BAND_THRESHOLDS.warm - 1 },
  cold: { min: 0, max: SCORE_BAND_THRESHOLDS.early - 1 },
};

/**
 * The band a 0–100 score falls in. Backend scoring, the seeder and the
 * dashboard's optimistic display all call this; nothing re-implements it.
 */
export function bandForScore(
  score: number,
  thresholds: ScoreBandThresholds = SCORE_BAND_THRESHOLDS,
): ScoreBand {
  if (score >= thresholds.hot) return 'hot';
  if (score >= thresholds.warm) return 'warm';
  if (score >= thresholds.early) return 'early';
  return 'cold';
}

export const SOURCES = ['form', 'assistant', 'import', 'api', 'referral'] as const;
export type Source = (typeof SOURCES)[number];

export const REGIONS = [
  'North America',
  'Latin America',
  'Europe',
  'Middle East',
  'Asia Pacific',
  'Africa',
] as const;
export type Region = (typeof REGIONS)[number];

/** The 16 inquiry paths from the marketing contact form. Kept verbatim. */
export const INQUIRY_TYPES = [
  'Submit Project Geometry',
  'Request Mold Review',
  'Request Project Review',
  'Discuss a Commercial Project',
  'Discuss an Industrial Application',
  'Discuss an Institutional Project',
  'General Contractor / CM Inquiry',
  'Modular Mold System Inquiry',
  'Custom Interior Finishes',
  'Automation Retrofit Review',
  'Request New Plant Planning Review',
  'Enterprise Plant Standardization',
  'Apply for Partner Review',
  'Partner Plant Review',
  'Enterprise / Multi-Region Inquiry',
  'General Contact',
] as const;
export type InquiryType = (typeof INQUIRY_TYPES)[number];

export const ACTIVITY_TYPES = [
  'created',
  'stage_changed',
  'status_changed',
  'assigned',
  'note_added',
  'task_created',
  'task_completed',
  'tag_added',
  'tag_removed',
  'email_sent',
  'email_received',
  'call_logged',
  'meeting_logged',
  'file_uploaded',
  'field_changed',
  'merged',
  'sla_breached',
  'viewed',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

/* ────────────────────────────────────────────────────────────────────────────
   Entities
   ──────────────────────────────────────────────────────────────────────────── */

export interface User {
  id: ID;
  email: string;
  name: string;
  role: Role;
  /** Deterministic accent index 0–7 for avatar tinting. */
  avatarSeed: number;
  title: string | null;
  isActive: boolean;
  lastSeenAt: ISODate | null;
  createdAt: ISODate;
}

export interface Company {
  id: ID;
  name: string;
  domain: string | null;
  website: string | null;
  region: Region | null;
  /** e.g. "Developer", "Precast Producer", "Architecture", "General Contractor" */
  segment: string | null;
  /** Headcount bucket: "1-10" | "11-50" | "51-200" | "201-1000" | "1000+" */
  size: string | null;
  city: string | null;
  country: string | null;
  createdAt: ISODate;
  updatedAt: ISODate;
  /** Denormalised rollups, present on detail reads. */
  stats?: {
    inquiries: number;
    contacts: number;
    openInquiries: number;
    lastActivityAt: ISODate | null;
  };
}

export interface Contact {
  id: ID;
  companyId: ID | null;
  company?: Company | null;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string | null;
  title: string | null;
  region: Region | null;
  avatarSeed: number;
  createdAt: ISODate;
  updatedAt: ISODate;
  stats?: {
    inquiries: number;
    lastInquiryAt: ISODate | null;
  };
}

export interface Tag {
  id: ID;
  name: string;
  /** Token name from the palette, not a raw hex: 'gold' | 'blue' | 'teal' | … */
  color: TagColor;
  count?: number;
}

export const TAG_COLORS = [
  'gold',
  'blue',
  'sky',
  'teal',
  'violet',
  'rose',
  'amber',
  'slate',
] as const;
export type TagColor = (typeof TAG_COLORS)[number];

export interface PipelineStage {
  id: ID;
  key: StageKey;
  label: string;
  position: number;
  color: TagColor;
  /**
   * The stage's colour, already resolved to the CSS custom property that paints
   * it — e.g. `'--stage-qualifying'`. See `stageRampToken` below.
   *
   * Required. It used to be optional because two endpoints hydrate stages
   * (`/pipeline/stages` and `/inquiries/board`) and only one sent it, which
   * meant the board — the surface the ramp exists for — never received a
   * resolved colour and every consumer fell back to a second rule of its own.
   * Both endpoints send it now, and the type no longer permits a stage that
   * does not know what colour it is.
   */
  colorToken: string;
  /** Terminal stages close the inquiry. */
  isTerminal: boolean;
  isWon: boolean;
  /** Target hours to move out of this stage; drives the SLA meter. */
  slaHours: number | null;
}

/* ────────────────────────────────────────────────────────────────────────────
   Stage colour — ONE definition, and it is a stage's POSITION

   A pipeline stage's colour is a step on the ordinal `--stage-*` ramp in
   `dashboard/src/styles/tokens.css`, NOT a step on the categorical tag/viz
   ramp. The distinction is the whole point: a tag hue means "which category",
   a stage hue means "how far along the pipeline". The ramp holds lightness
   near-constant inside a theme and separates its steps by hue — cyan → blue →
   periwinkle → orchid → magenta — with `--success` and a neutral at the two
   terminals.

   There used to be TWO rules. The server resolved `colorToken` from the stored
   `pipeline_stages.color` swatch through a `STAGE_COLOR_TOKENS` map, and the
   dashboard ignored that field and resolved the hue from `position` instead.
   They agreed only because the seed happens to store the swatch that matches
   each stage's seeded rank; a reorder in Settings → Pipeline broke the tie and
   the two halves of the product disagreed about what colour a stage was. The
   swatch map also was not a translation — it mapped the name `amber` onto
   `--stage-review` (an orchid) and both `gold` and `rose` onto
   `--stage-proposal`, so two names painted one colour and no choice in
   Settings could make a column any hue its own label implied.

   So there is now one rule and it is the one that was actually being rendered:
   **step N of the ramp for the Nth in-flight stage, `--stage-won` /
   `--stage-lost` for the two terminals.** `pipeline_stages.color` is still
   stored (its CHECK constraint is fixed and the column is what older writes
   populate) but it no longer paints anything, which is the only honest thing to
   do with a stored value that could contradict the encoding.

   `stageRampToken` is the sole implementation. The API resolves it once into
   `PipelineStage.colorToken` on every endpoint that hydrates a stage, and every
   surface that paints a stage — board column heads, board card meta, inbox
   stage cells, the record header and its picker, the tasks queue, the contact
   and company inquiry tables, the funnel, the volume chart, the settings
   preview — reads that single value.
   ──────────────────────────────────────────────────────────────────────────── */

/** The five in-flight ramp steps, in pipeline order. */
export const STAGE_RAMP_TOKENS = [
  '--stage-new',
  '--stage-triage',
  '--stage-qualifying',
  '--stage-review',
  '--stage-proposal',
] as const;

export const STAGE_WON_TOKEN = '--stage-won';
export const STAGE_LOST_TOKEN = '--stage-lost';

/**
 * A stage's colour, from its rank on the pipeline.
 *
 * `position` is 1-based, exactly as `PipelineStage.position` is on the wire. A
 * sixth or later in-flight stage clamps to the deepest open step rather than
 * introducing a hue from outside the closed ramp.
 */
export function stageRampToken(
  position: number,
  isTerminal: boolean,
  isWon: boolean,
): string {
  if (isTerminal) return isWon ? STAGE_WON_TOKEN : STAGE_LOST_TOKEN;
  const index = Math.min(
    Math.max(Math.trunc(position) - 1, 0),
    STAGE_RAMP_TOKENS.length - 1,
  );
  return STAGE_RAMP_TOKENS[index] as string;
}

/**
 * The colour each canonical stage ships with. It is *storage*, not paint: the
 * seed writes it so the column is never null, and `stageRampToken` decides what
 * every surface actually renders.
 */
export const DEFAULT_STAGE_COLORS = {
  new: 'blue',
  triage: 'sky',
  qualifying: 'violet',
  review: 'amber',
  proposal: 'gold',
  won: 'teal',
  lost: 'slate',
} as const satisfies Record<StageKey, TagColor>;

/* ────────────────────────────────────────────────────────────────────────────
   Counts — one definition

   THE RULE. An inquiry is counted when its status is one of `COUNTED_STATUSES`
   — `open`, `won` or `lost`. It is excluded when its status is `spam` or
   `archived`.

   COUNTED   open   still in play
             won    closed with an award; the funnel's denominator needs it
             lost   closed without one; ditto
   EXCLUDED  spam   never was an inquiry
             archived  deliberately taken out of the working set

   Nothing else narrows a count. There is no default date window: "inquiries"
   means every counted row ever, and a date range is only applied where the user
   picked one (analytics, an explicit filter). A filtered list still reports the
   total for its own filter set — that is a different question, asked out loud —
   but the unfiltered figure any surface prints is this one.

   This is what "911 inquiries" got wrong. 911 is every row in the table,
   including 36 spam and 109 archived; the board's per-stage totals counted the
   same way while Settings counted only `open`, so New read 83 in one place and
   47 in the other in a single session. Under the rule above they agree.

   `GET /pipeline/counts` returns exactly this shape. It is not, however, the
   only endpoint that may be asked: `GET /inquiries`, `GET /inquiries/board`,
   `GET /saved-views/counts`, `GET /export/inquiries.csv` and the `/analytics/*`
   scope all run the *same* predicate through the same function
   (`inquiryScopeSql` in `backend/src/routes/inquiries.ts`), so they cannot
   disagree with it or with each other. On the seeded data every one of them
   answers 766, and New is 42 everywhere it is printed.

   The rule stands down in exactly one case, and only because the user asked for
   it out loud: a request whose own filters name `status` gets the rows it named.
   That is how the sidebar's "Spam review (36)" can exist beside an inbox that
   holds none of them.
   ──────────────────────────────────────────────────────────────────────────── */

export const COUNTED_STATUSES = ['open', 'won', 'lost'] as const satisfies readonly Status[];
export const UNCOUNTED_STATUSES = ['spam', 'archived'] as const satisfies readonly Status[];

export interface StageCount {
  stageKey: StageKey;
  /** Counted inquiries sitting in this stage. */
  total: number;
  /** Subset of `total` whose status is still `open`. */
  open: number;
  /**
   * Subset of `total` past its response target with no first response yet —
   * computed over the whole stage, server-side, so no surface has to print a
   * breach count qualified by how many cards it happened to load.
   */
  overdue: number;
}

export interface PipelineCounts {
  /** Counted inquiries across every stage — the product's one headline number. */
  total: number;
  open: number;
  overdue: number;
  /** In pipeline order. */
  stages: StageCount[];
  /** Named on the wire so a surface can explain the figure without guessing. */
  excludedStatuses: readonly Status[];
  /** When these were read, so a stale cache is visible rather than silent. */
  asOf: ISODate;
}

export interface Attachment {
  id: ID;
  inquiryId: ID;
  filename: string;
  mime: string;
  /** Bytes. */
  size: number;
  sha256: string;
  uploadedById: ID | null;
  createdAt: ISODate;
  /** Server-signed download path, relative to the API root. */
  downloadUrl: string;
}

/** One qualification conversation captured by the site assistant. */
export interface AssistantSession {
  id: ID;
  inquiryId: ID | null;
  flowKey: string;
  answers: Record<string, string>;
  routeLabel: string | null;
  routeUrl: string | null;
  /** Band the client-side heuristic produced ("Hot" | "Warm" | "Early"). */
  clientScore: string | null;
  transcript: TranscriptEntry[];
  startedAt: ISODate;
  completedAt: ISODate | null;
}

export interface TranscriptEntry {
  role: 'assistant' | 'user';
  text: string;
  at: ISODate | null;
}

export interface ScoreFactor {
  /** Human label, e.g. "Timeline: within 3 months". */
  label: string;
  /** Signed contribution to the 0–100 score. */
  weight: number;
  /** Machine key for filtering/analytics. */
  key: string;
}

export interface Inquiry {
  id: ID;
  /** Short human reference shown in the UI, e.g. "AF-2418". */
  ref: string;
  type: InquiryType;
  subject: string;
  message: string;
  source: Source;
  status: Status;
  stageKey: StageKey;
  priority: Priority;
  region: Region | null;

  /** 0–100, recomputed server-side on every write. */
  score: number;
  scoreBand: ScoreBand;
  scoreFactors: ScoreFactor[];

  contactId: ID | null;
  contact?: Contact | null;
  companyId: ID | null;
  company?: Company | null;
  ownerId: ID | null;
  owner?: User | null;

  tags: Tag[];
  attachments?: Attachment[];
  assistantSession?: AssistantSession | null;

  /**
   * Estimated contract value in whole USD, or `null` when nobody has put a
   * number on it yet.
   *
   * It is a **stored field on the record**, not a figure analytics invents.
   * Analytics' "Open pipeline" tile used to sum a SQL expression that derived an
   * amount from the inquiry type and the company's headcount band — a model, on
   * a screen that presented it as a measurement, drilling into a list sorted by
   * lead score because there was no value to sort by. $308M traced to nothing a
   * reader could open.
   *
   * Now: the seeder writes it, `PATCH /inquiries/:id` edits it, the record
   * prints it, the grid has a `value` column, `FIELD_MAP` filters and sorts on
   * it, the CSV exports it, and every `/analytics/*` figure sums this column.
   * Rows nobody has valued contribute 0 and are reachable through the
   * `value is_empty` filter, so the shortfall is visible rather than modelled
   * away.
   */
  value: number | null;

  /** Free-form extras captured by the form (budget, timeline, sqft, …). */
  fields: Record<string, string | number | boolean | null>;

  /** Response-time SLA. */
  slaDueAt: ISODate | null;
  firstResponseAt: ISODate | null;
  /** Minutes from creation to first human response; null while unanswered. */
  firstResponseMinutes: number | null;

  utm: {
    source: string | null;
    medium: string | null;
    campaign: string | null;
    term: string | null;
    content: string | null;
    referrer: string | null;
    landingPage: string | null;
  };

  spamScore: number;
  isDuplicateOf: ID | null;

  createdAt: ISODate;
  updatedAt: ISODate;
  closedAt: ISODate | null;
  lastActivityAt: ISODate;

  counts?: {
    notes: number;
    tasks: number;
    openTasks: number;
    attachments: number;
    activities: number;
  };
}

export interface Note {
  id: ID;
  inquiryId: ID;
  authorId: ID;
  author?: User;
  body: string;
  isPinned: boolean;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface Task {
  id: ID;
  inquiryId: ID;
  inquiryRef?: string;
  title: string;
  assigneeId: ID | null;
  assignee?: User | null;
  dueAt: ISODate | null;
  completedAt: ISODate | null;
  priority: Priority;
  createdById: ID;
  createdAt: ISODate;
}

export interface Activity {
  id: ID;
  inquiryId: ID;
  actorId: ID | null;
  actor?: User | null;
  type: ActivityType;
  /** Rendered summary, e.g. "moved from Triage to Qualifying". */
  body: string;
  meta: Record<string, unknown>;
  createdAt: ISODate;
}

export interface SavedView {
  id: ID;
  name: string;
  entity: 'inquiries' | 'contacts' | 'companies' | 'tasks';
  filters: FilterSet;
  sort: SortSpec[];
  /** Ordered visible column ids. */
  columns: string[];
  layout: 'table' | 'board' | 'split';
  ownerId: ID;
  isShared: boolean;
  isDefault: boolean;
  icon: string | null;
  position: number;
  createdAt: ISODate;
}

export interface AuditEntry {
  id: ID;
  actorId: ID | null;
  actor?: User | null;
  entity: string;
  entityId: ID;
  action: 'create' | 'update' | 'delete' | 'login' | 'logout' | 'export' | 'bulk';
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  createdAt: ISODate;
}

/* ────────────────────────────────────────────────────────────────────────────
   Query contract (list endpoints)
   ──────────────────────────────────────────────────────────────────────────── */

export type FilterOperator =
  | 'is'
  | 'is_not'
  | 'in'
  | 'not_in'
  | 'contains'
  | 'not_contains'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'is_empty'
  | 'is_not_empty';

export interface FilterCondition {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string | number | boolean | null | Array<string | number>;
}

export interface FilterSet {
  /** How the top-level conditions combine. */
  combinator: 'and' | 'or';
  conditions: FilterCondition[];
}

export interface SortSpec {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ListQuery {
  /** Full-text query across ref, subject, message, contact, company. */
  q?: string;
  filters?: FilterSet;
  sort?: SortSpec[];
  cursor?: string;
  limit?: number;
  /** Applies a stored view's filters + sort before any inline overrides. */
  viewId?: ID;
}

/* ────────────────────────────────────────────────────────────────────────────
   Analytics
   ──────────────────────────────────────────────────────────────────────────── */

export type RangeKey = '7d' | '30d' | '90d' | '12m' | 'ytd' | 'all' | 'custom';

export interface MetricDelta {
  value: number;
  /** Same-length preceding window. */
  previous: number;
  /** Fractional change, e.g. 0.184 for +18.4%. Null when previous is 0. */
  change: number | null;
  /** Whether an increase is good — drives colour, not the arrow direction. */
  polarity: 'up-good' | 'down-good' | 'neutral';
  /** Sparkline points, oldest → newest. */
  spark: number[];
}

export interface AnalyticsOverview {
  range: { from: ISODate; to: ISODate; key: RangeKey };
  inquiries: MetricDelta;
  qualified: MetricDelta;
  won: MetricDelta;
  /**
   * Inquiries that reached a decision (won or lost) — the denominator under
   * `winRate`, on the wire for the same reason `LeaderboardRow.decided` is: a
   * rate without its sample size cannot be reconciled with the counts printed
   * beside it. The Win rate tile prints "29 won of 175 decided" from it, so the
   * headline row and the leaderboard 1,500px below can never disagree about
   * what the rate was taken over.
   */
  decided: MetricDelta;
  winRate: MetricDelta;
  medianFirstResponseMinutes: MetricDelta;
  slaCompliance: MetricDelta;
  avgScore: MetricDelta;
  pipelineValue: MetricDelta;
  /**
   * How much of the pipeline figure above is actually populated: `valued` rows
   * carry an `Inquiry.value`, out of `total` rows in the same scope.
   *
   * On the wire because a sum is only honest beside its coverage. The tile
   * prints it, and the shortfall is one click away — `value is_empty` is a real
   * filter on a real column.
   */
  pipelineValueCoverage: { valued: number; total: number };
}

export interface TimeseriesPoint {
  /** Bucket start. */
  t: ISODate;
  [series: string]: number | string;
}

export interface TimeseriesResponse {
  granularity: 'hour' | 'day' | 'week' | 'month';
  series: Array<{ key: string; label: string; total: number }>;
  points: TimeseriesPoint[];
}

export interface BreakdownRow {
  key: string;
  label: string;
  count: number;
  /** 0–1 share of total. */
  share: number;
  won: number;
  winRate: number;
  avgScore: number;
  medianResponseMinutes: number | null;
}

export interface FunnelStep {
  key: string;
  label: string;
  count: number;
  /** Conversion from the previous step, 0–1. */
  stepRate: number;
  /** Conversion from the first step, 0–1. */
  totalRate: number;
  medianHoursInStep: number | null;
}

export interface LeaderboardRow {
  user: User;
  assigned: number;
  won: number;
  /**
   * Inquiries that reached a decision (won or lost) — the denominator under
   * `winRate`. On the wire rather than re-derived from `won / winRate`, because
   * that division is undefined at a win rate of 0 and a rate without its sample
   * size is the difference between 3-of-3 and 40-of-40.
   */
  decided: number;
  winRate: number;
  medianFirstResponseMinutes: number | null;
  slaCompliance: number;
}

export interface AnalyticsResponseTimes {
  /**
   * Median minutes per weekday×hour cell, for the heatmap.
   *
   * `count` is **arrivals** in the cell; `replyCount` is how many of them have
   * a measured first reply, which is the sample `medianMinutes` was taken from
   * and the only honest weight for a roll-up across cells. The two are not the
   * same number — 14 of the 388 inquiries in the default window have never been
   * answered — and treating arrivals as replies is what let one 43-hour outlier
   * in a five-arrival cell name "the slowest window".
   */
  heatmap: Array<{
    weekday: number;
    hour: number;
    count: number;
    replyCount: number;
    medianMinutes: number | null;
  }>;
  histogram: Array<{ bucket: string; count: number }>;
  /** Inquiries in the window (the heatmap's `count`, summed). */
  arrivals: number;
  /** …of which have had a first reply (the histogram's counts, summed). */
  replies: number;
  p50: number | null;
  p90: number | null;
  p99: number | null;
}

/* ────────────────────────────────────────────────────────────────────────────
   Webhooks

   Outbound delivery is real: `backend/src/lib/webhooks.ts` subscribes to the
   same event bus the SSE stream reads, signs each body with the endpoint's
   secret and records one `webhook_deliveries` row per attempt.

   `WEBHOOK_EVENT_TYPES` is therefore exactly the set of events the bus actually
   publishes. Settings used to offer eleven — `inquiry.stage_changed`,
   `inquiry.won`, `sla.breached` and so on — none of which exist anywhere in the
   product, on a page whose endpoints lived in the reader's own localStorage and
   whose deliveries were never attempted. A subscription form must not name an
   event that can never fire.
   ──────────────────────────────────────────────────────────────────────────── */

export const WEBHOOK_EVENT_TYPES = [
  'inquiry.created',
  'inquiry.updated',
  'inquiry.deleted',
  'activity.created',
  'note.created',
  'task.updated',
] as const;
export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'success', 'failed'] as const;
export type WebhookDeliveryStatus = (typeof WEBHOOK_DELIVERY_STATUSES)[number];

/** Consecutive failures after which the server pauses an endpoint by itself. */
export const WEBHOOK_FAILURE_LIMIT = 5;

export interface WebhookDelivery {
  id: ID;
  webhookId: ID;
  event: WebhookEventType | string;
  status: WebhookDeliveryStatus;
  statusCode: number | null;
  /** First 500 characters of what the receiver returned. */
  responseBody: string | null;
  error: string | null;
  /** 1-based; a retried delivery records one row per attempt. */
  attempt: number;
  durationMs: number | null;
  createdAt: ISODate;
  deliveredAt: ISODate | null;
}

export interface Webhook {
  id: ID;
  name: string;
  url: string;
  /**
   * The signing secret. Returned in full to an admin — it is workspace
   * configuration, not a credential the server keeps from its own operators —
   * and never to a lower role, which receives `null`.
   */
  secret: string | null;
  events: WebhookEventType[];
  isActive: boolean;
  /** Consecutive failures. Reset by any success; at `WEBHOOK_FAILURE_LIMIT` the endpoint pauses. */
  failureCount: number;
  lastDeliveryAt: ISODate | null;
  createdById: ID | null;
  createdAt: ISODate;
  updatedAt: ISODate;
  /** Most recent attempts, newest first. Present on the list read. */
  deliveries?: WebhookDelivery[];
}

export interface WebhookInput {
  name: string;
  url: string;
  events: WebhookEventType[];
}

export interface WebhookPatch {
  name?: string;
  url?: string;
  events?: WebhookEventType[];
  isActive?: boolean;
  /** Mint a new signing secret. */
  rotateSecret?: boolean;
}

/* ────────────────────────────────────────────────────────────────────────────
   Realtime
   ──────────────────────────────────────────────────────────────────────────── */

export type RealtimeEvent =
  | { type: 'inquiry.created'; inquiry: Inquiry }
  | { type: 'inquiry.updated'; inquiry: Inquiry }
  | { type: 'inquiry.deleted'; id: ID }
  | { type: 'activity.created'; activity: Activity }
  | { type: 'note.created'; note: Note }
  | { type: 'task.updated'; task: Task }
  | { type: 'presence'; users: Array<{ id: ID; name: string; at: ISODate }> }
  | { type: 'heartbeat'; at: ISODate };

/* ────────────────────────────────────────────────────────────────────────────
   Requests
   ──────────────────────────────────────────────────────────────────────────── */

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SessionResponse {
  user: User;
  permissions: string[];
  /**
   * The account is on a password an administrator generated and has never been
   * changed. The dashboard holds the user on the change-password screen until
   * this is false.
   */
  mustChangePassword: boolean;
  /**
   * How many people are in the workspace, including deactivated ones.
   *
   * A layout fact, not a metric: the session probe resolves before any route
   * mounts, so a surface that has to reserve room for the roster (`/settings/
   * team`) knows the exact height of the block it is about to draw *before*
   * `GET /users` answers, instead of guessing and then jumping. It is the same
   * population `GET /users` returns.
   */
  userCount: number;
  /**
   * Workspace-level configuration the shell and Settings need before any route
   * mounts. Read from `settings` (overridable by env), never from the browser.
   */
  workspace: {
    /** Display name — `settings['org.name']`. */
    name: string;
    /**
     * The origin the public marketing site posts into: the workspace's own
     * hostname, e.g. `https://crm.alphaform.group`.
     *
     * `null` when the deployment has not been told what it is reachable as.
     * That is a real state and Settings says so out loud, because the
     * alternative — printing whatever origin the reader's browser happens to be
     * on — is how `http://127.0.0.1:21022/api/v1/public/inquiries` came to be
     * published as the address a production marketing site should post to.
     */
    publicOrigin: string | null;
    /**
     * True when `publicOrigin` came from the *development* fallback rather than
     * from configuration, so the UI can label it instead of presenting a
     * loopback address as the production endpoint.
     */
    publicOriginIsDevDefault: boolean;
  };
}

export type BulkAction =
  | { action: 'assign'; ownerId: ID | null }
  | { action: 'stage'; stageKey: StageKey }
  | { action: 'status'; status: Status }
  | { action: 'priority'; priority: Priority }
  | { action: 'tag_add'; tagIds: ID[] }
  | { action: 'tag_remove'; tagIds: ID[] }
  | { action: 'delete' };

export interface BulkRequest {
  ids: ID[];
  op: BulkAction;
}

export interface BulkResponse {
  updated: number;
  failed: Array<{ id: ID; reason: string }>;
}

/** One id the server could not write, and why. */
export interface BulkFailure {
  id: ID;
  reason: string;
}

/**
 * A batch write over tasks — the response `PATCH /tasks` and
 * `POST /tasks/complete` both return.
 *
 * A bulk edit is *one* request, so it settles once: one optimistic patch, one
 * rollback snapshot, one toast. `tasks` carries the rows as they now stand so
 * the cache can be reconciled without a refetch per row, and `failed` names the
 * ids the write could not reach — a batch is partial-tolerant by design, the
 * same contract `POST /inquiries/bulk` has always had.
 */
export interface TaskBatchResponse {
  tasks: Task[];
  failed: BulkFailure[];
}

/** `DELETE /tasks` — the ids that went, and the ones that could not. */
export interface TaskBatchDeleteResponse {
  deleted: ID[];
  failed: BulkFailure[];
}

/** Payload posted by the public marketing form (multipart when files present). */
export interface PublicInquiryPayload {
  type: InquiryType;
  region: string;
  name: string;
  email: string;
  company?: string;
  phone?: string;
  role?: string;
  message: string;
  fields?: Record<string, string>;
  utm?: Partial<Inquiry['utm']>;
  consent: boolean;
  /** Honeypot — must be empty. */
  website?: string;
}

/** Payload posted when the site assistant completes a flow. */
export interface PublicAssistantPayload {
  flow: string;
  answers: Record<string, string>;
  lead: Record<string, string>;
  score: string;
  route: { label: string; url: string };
  transcript: Array<{ role: string; text: string }>;
}

/* ────────────────────────────────────────────────────────────────────────────
   Bug reports
   ────────────────────────────────────────────────────────────────────────────

   The one snake_case island in this file, and the one place timestamps are unix
   seconds rather than ISO strings. Both are the wire format of the vendored
   reporter widget (`dashboard/public/bugkit.js`), which is copied verbatim from
   the `bug-report-system` skill so it can be replaced wholesale when the skill
   updates. Renaming the fields here would mean forking it. See the header of
   `backend/src/routes/bugs.ts`.

   The three classification axes — page × nature × reportType — are captured at
   report time, never inferred later. `page` is the grouping key and maps 1:1 to
   a source file; that table lives in the project CLAUDE.md. It is a bare string
   rather than a union on purpose: a new screen has to group correctly before
   anyone edits this file. */

export type BugSeverity = 'low' | 'medium' | 'high' | 'crash';

/** `resolved` · `wontfix` · `duplicate` are archived, not deleted. */
export type BugStatus = 'new' | 'triaged' | 'in_progress' | 'resolved' | 'wontfix' | 'duplicate';

/** Design reports batch into one styling pass; functional ones need a repro. */
export type BugNature = 'functional' | 'design';

/** Suggestions get the same capture and triage, and never inflate the bug count. */
export type BugReportType = 'bug' | 'suggestion';

/** One line of the reporter's console ring buffer. Captured verbatim. */
export interface BugConsoleLine {
  t: number;
  level: string;
  msg: string;
}

/** A failed request. Method, URL, status and duration only — never bodies. */
export interface BugNetworkLine {
  t: number;
  method: string;
  url: string;
  status: number;
  ms: number;
}

/**
 * The silent capture. Admin-only: it carries IP addresses, referrers and
 * console lines that can contain another user's data, which is why
 * `GET /bugs/mine` omits it.
 */
export interface BugContext {
  /** The reporter's own account of the page. A hint, not evidence. */
  client?: {
    page?: string;
    title?: string;
    path?: string;
    query?: string;
    hash?: string;
    page_key?: string;
    referrer?: string;
    viewport?: string;
    screen?: string;
    ua?: string;
    locale?: string;
    tz?: string;
    app?: string;
    captured_at?: string;
    console?: BugConsoleLine[];
    network?: BugNetworkLine[];
    [key: string]: unknown;
  };
  /** What the server saw. The load-bearing view. */
  server?: {
    client_ip?: string;
    request_ua?: string | null;
    request_referer?: string | null;
    user_id?: string;
    submitted_at?: number;
  };
}

/** The reporter's own view of a report they filed — status, never evidence. */
export interface BugReportMine {
  id: number;
  title: string;
  severity: BugSeverity;
  status: BugStatus;
  verified: 0 | 1;
  verified_at: number | null;
  screenshot_count: number;
  created_at: number;
  updated_at: number;
  page: string;
  nature: BugNature;
  report_type: BugReportType;
}

/** A row of the admin list. `context_json` is raw; the detail route parses it. */
export interface BugReport extends BugReportMine {
  user_id: string | null;
  description: string;
  admin_notes: string;
  resolved_at: number | null;
  context_json?: string;
  author_email?: string;
  author_name?: string;
}

/** One report, opened. */
export interface BugReportDetail extends Omit<BugReport, 'context_json'> {
  context: BugContext;
  screenshots: string[];
}

/** One page's worth of reports, which is how triage is actually read. */
export interface BugPageGroup {
  page: string;
  label: string;
  total: number;
  /** `status` outside the three archived values. */
  open: number;
  by_status: Record<string, number>;
  by_nature: Record<string, number>;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  items: BugReportMine[];
}

export interface BugSummary {
  /** Most-open-first: "where are we at, screen by screen". */
  pages: BugPageGroup[];
  totals: {
    pages: number;
    total: number;
    open: number;
    /** `status = 'new'` — nobody has looked yet. */
    unread: number;
    /** Open and unconfirmed-on-live. The rail badge counts these. */
    unverified: number;
  };
}

/** What triage may change. Reclassification included — reporters mistag. */
export interface BugPatch {
  status?: BugStatus;
  admin_notes?: string;
  page?: string;
  nature?: BugNature;
  report_type?: BugReportType;
}

/** What the widget posts. Only `title` is required; capture never blocks it. */
export interface BugSubmitPayload {
  title: string;
  description?: string;
  severity?: BugSeverity;
  nature?: BugNature;
  report_type?: BugReportType;
  page?: string | null;
  context?: BugContext['client'];
}
